import React from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import _ from 'underscore';

import PrimaryButton from '../../../PrimaryButton';
import ImageSlider from './ImageSlider';
import DishModification from './DishModification';
import { f$ } from '../../../../helpers/formatPrice';
import l from '../../../../helpers/lang';
import { productProps, settingsProps } from '../../../../helpers/propTypes';
import Cart from '../../../../helpers/cart';
import Storage from '../../../../helpers/storage';
import { STORAGE_CART } from '../../../../helpers/constants';
import Navbar from '../../../Navbar';
import ProductTags from '../../../product/ProductTags';
import FloatingNavigation from '../../../navigation/FloatingNavigation';
import { isUseDeliveryMode } from '../../../../helpers/catalog';

export default class ProductPage extends React.Component {
    static propTypes = {
      router: PropTypes.shape({}).isRequired,
      product: productProps.isRequired,
      settings: settingsProps.isRequired,
      backUrl: PropTypes.string,
    };

    static defaultProps = {
      backUrl: '/',
    };

    constructor(props) {
      super(props);

      this.state = {
        sum: props.product.price,
        hasErrors: false,
        firstSubmit: true,
      };
    }

    componentDidMount() {
      if (this.dishModificationElem) {
        // Сразу при загрузке проводим валидацию, чтобы нельзя было добавить пустой модификатор
        this.setState({
          hasErrors: !this.dishModificationElem.isValid(),
        });
      }
    }

    changeDishModify = async (modification, hasErrors) => {
      const sum = this.dishModificationElem.getTotalSum();

      this.setState({ sum, hasErrors, modification });
    }

    addToCart = _.throttle(async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const { product, router, backUrl } = this.props;
      const { modification, hasErrors } = this.state;

      if (hasErrors) {
        this.setState({ firstSubmit: false }, () => {
          const dishValidErrors = window.document.getElementsByClassName('dish-valid-error');
          if (dishValidErrors.length > 0) {
            dishValidErrors[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });

        return;
      }

      const cartProduct = {
        productId: product.product_id,
        modification: product.group_modifications ? modification : undefined,
        modificator_id: product.productModifications ? modification[0].m : undefined,
        count: product.isWeight ? 100 : 1,
      };

      const cart = Storage.get(STORAGE_CART);
      const i = Cart.getProductIndex({ cart, ...cartProduct });
      if (i !== -1) {
        cartProduct.count += cart.products[i].count;
      }

      Cart.changeProducts(cartProduct);

      // Закрываем окно
      router.push(backUrl, undefined, { scroll: false });

      this.setState({ firstSubmit: false });
    }, 500)

    /**
     * Чтобы не писать дубликат компоненты с для товаров с модиф. мы просто представляем товар как тех. карту
     * @returns {*}
     */
    getDish = () => {
      const { product } = this.props;

      if (product.group_modifications) {
        return product;
      }

      return {
        price: 0,
        group_modifications: [{
          dish_modification_group_id: 1,
          name: '',
          num_min: 1,
          num_max: 1,
          type: 1,
          modifications: product.productModifications.map((m) => ({
            dish_modification_id: m.modificator_id,
            name: m.name,
            price: m.price,
            hidePlus: true,
          })),
        }],
      };
    }

    getButtonText = () => {
      const { sum, firstSubmit, hasErrors } = this.state;

      // Ошибка может быть только если есть обязательная модификация
      if (hasErrors && !firstSubmit) {
        return l('delivery.product.buttonSelectModification');
      }

      // Кейс когда все в порядке
      return l('delivery.product.addToCartButton', { sum: f$(sum) });
    }

    render() {
      const { product, router, settings } = this.props;
      const { firstSubmit, hasErrors } = this.state;
      const { showModifications, modal } = router.query;
      const { showCart } = settings;
      const isDeliveryMode = showCart && isUseDeliveryMode();

      return (
        <>
          <Head>
            <title>{product.seoTitle || product.name}</title>
            <meta property="og:type" content="restaurant.menu_item" />
            <meta property="og:title" content={product.seoTitle || product.name} />
            <meta property="og:url" content={`/product/${product.pos_id}`} />
            <meta property="og:image" content={product.photo} />
            <meta property="og:description" content={product.seoDescription} />
            <meta name="description" content={product.seoDescription} />
            <meta property="restaurant:section" content="" />
            <meta property="restaurant:variation:price:amount" content={product.price} />
          </Head>

          {!modal && (
            <div className="mt-2 mb-4">
              <FloatingNavigation>
                <Navbar backUrl="/" backTitle={l('delivery.product.backToMenu')} />
              </FloatingNavigation>
            </div>
          )}
          <div className={`container px-5 px-md-7 pt-md-5 flex-grow-1 ${!isDeliveryMode && 'pb-4'} `}>
            <div className="row">
              <div className={`col-sm-12 col-md-5 ${showModifications && 'd-none d-md-block'}`}>
                <ImageSlider images={[{ id: 1, url: product.photo, alt: product.name }]} />
              </div>

              <div className="col-sm-12 col-md-7">
                <h1 data-testid="product_name_in_popup" className="mt-5 mb-3">{product.name}</h1>

                <ProductTags product={product} />

                {Boolean(product.description) && !showModifications && (
                <div
                  className="text-muted text-break overflow-hidden my-2"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
                )}
                {!product.group_modifications && !product.productModifications && (
                <div className="text-muted p-0 mt-3 h4">
                  {f$(product.price)}
                  {Boolean(product.isWeight) && `\u00A0${l('delivery.index.for100g')}`}
                </div>
                )}

                {Boolean(product.group_modifications || product.productModifications) && (
                <div className="mt-7">
                  <DishModification
                    ref={(elm) => this.dishModificationElem = elm}
                    dish={this.getDish()}
                    onChangeModification={this.changeDishModify}
                    modification={[]}
                    firstSubmit={firstSubmit}
                    showCart={isDeliveryMode}
                  />
                </div>
                )}
              </div>
            </div>
          </div>

          {isDeliveryMode && (
            <div className="px-4 pb-4 pt-3 border-top sticky-bottom bg-body">
              <div className="container">
                <div className="row">
                  <div className="col-sm-12 col-md-7 offset-md-5">
                    <PrimaryButton
                      text={this.getButtonText()}
                      onClick={this.addToCart}
                      isDisabled={!firstSubmit && hasErrors}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }
}
